import React, { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { matchPath, useLocation } from 'react-router-dom';

import { useChatSubmitNavigate } from '~/hooks/use-chat-submit-navigate/useChatSubmitNavigate';
import { useNavigateToAssetOverview } from '~/hooks/use-navigate-to-asset-overview/useNavigateToAssetOverview';
import { useTerminalNavigate } from '~/hooks/use-terminal-navigate/useTerminalNavigate';
import { appPaths } from '~/routes/app-paths';
import { Source } from '~/stores/use-chat/use-chat.types';
import { useChat } from '~/stores/use-chat/useChat';

import { ChatDropdown } from '../chat-dropdown-item/ChatDropdownItem';
import { TagSubmit } from '../tag-submit/TagSubmit';

export interface ChatSubmitProps {
  className?: string;
  placeholder?: string;
  submitCallback?: (message: string) => void;
  stopResponseCallback?: () => void;
  isSimpleVersion?: boolean;
  chatContext?: {
    target: Source;
    documentName?: string;
  };
}

export const ChatSubmit = ({
  submitCallback,
  chatContext,
  stopResponseCallback,
  isSimpleVersion,
  placeholder,
}: ChatSubmitProps) => {
  const { t } = useTranslation(['chat', 'intel', 'common']);
  const location = useLocation();
  const inputRef = useRef<HTMLTextAreaElement>(null);

  const navigateTerminal = useTerminalNavigate();
  const chatSubmitNavigation = useChatSubmitNavigate();
  const { navigateToChatMessage } = useNavigateToAssetOverview();

  const { sendMessage, isMessagePending, resetStore, messages, abortRequest } =
    useChat();

  useEffect(() => {
    const lastMessage = messages[messages.length - 1];
    chatSubmitNavigation(lastMessage);
  }, [messages, chatSubmitNavigation]);

  const submitHandler = (inputValue: string, clearDropdown: () => void) => {
    if (isMessagePending) {
      return;
    }
    if (inputValue) {
      const question = inputValue.trim();
      if (chatContext?.target) {
        const formattedQuestion = `${question}[[${chatContext.documentName}]]`;
        sendMessage({
          question: formattedQuestion,
          target: chatContext.target,
        });
      } else {
        sendMessage({ question });
      }
      clearDropdown();
      if (inputRef.current) {
        inputRef.current.rows = 1;
      }
    }
  };

  const handleChatSubmit = (
    event: React.FormEvent<HTMLFormElement>,
    inputText: string,
    clearDropdown: () => void
  ) => {
    event.preventDefault();
    if (isMessagePending) {
      stopResponseCallback?.();
      return;
    }
    submitHandler(inputText, clearDropdown);
    submitCallback?.(inputText.trim());
  };

  const onReset = () => {
    resetStore();
  };

  const handleKeydown = (
    _: React.KeyboardEvent<HTMLElement>,
    inputText: string,
    clearDropdown: () => void
  ) => {
    submitHandler(inputText, clearDropdown);
  };

  const handSelectItem = (item: ChatDropdown, clear: () => void) => {
    const isAlfredPage = matchPath(
      { path: appPaths.alfred, end: false, caseSensitive: false },
      location.pathname
    );
    const crumbProps = isAlfredPage
      ? {}
      : {
          crumb: {
            label: t('common:sideNavigation.alfred'),
            isAccessibleFromSideBar: true,
          },
        };
    navigateTerminal({
      navigateFn: () => navigateToChatMessage(item.tag),
      ...crumbProps,
    });
    clear();
  };

  return (
    <TagSubmit
      ref={inputRef}
      headerText={t('chat:dropdown.header')}
      onSubmit={handleChatSubmit}
      onKeyDown={handleKeydown}
      isLoading={isMessagePending}
      onAbort={abortRequest}
      onReset={onReset}
      onSelect={handSelectItem}
      isSimpleVersion={isSimpleVersion}
      placeholder={placeholder}
    />
  );
};
